/**
 * Diverse tipologie di rendering waveform
 * Caricamento lazy - solo quando necessario
 * 
 * @since v3.1.0 - Waveform types implementation
 */

/**
 * Bar Waveform (Default)
 * Barre verticali dal basso
 */
export function renderBar(ctx, data, colors) {
  const { width, height, bars } = data;
  const { waveformColor, waveformBgColor } = colors;
  
  // Pulisci canvas
  ctx.clearRect(0, 0, width, height);
  
  // Sfondo
  ctx.fillStyle = waveformBgColor;
  ctx.fillRect(0, 0, width, height);
  
  // Barre
  ctx.fillStyle = waveformColor;
  bars.forEach(bar => {
    ctx.fillRect(bar.x, bar.y, bar.width, bar.height);
  });
}

/**
 * Mirrored Waveform
 * Barre specchiate verticalmente (stile DAW)
 */
export function renderMirrored(ctx, data, colors) {
  const { width, height, bars } = data;
  const { waveformColor, waveformBgColor } = colors;
  
  // Pulisci canvas
  ctx.clearRect(0, 0, width, height);
  
  // Sfondo
  ctx.fillStyle = waveformBgColor;
  ctx.fillRect(0, 0, width, height);
  
  // Centro canvas
  const centerY = height / 2;
  
  // Barre mirrored
  ctx.fillStyle = waveformColor;
  bars.forEach(bar => {
    const halfHeight = bar.height / 2;
    
    // Barra superiore (specchiata)
    ctx.fillRect(bar.x, centerY - halfHeight, bar.width, halfHeight);
    
    // Barra inferiore (normale)
    ctx.fillRect(bar.x, centerY, bar.width, halfHeight);
  });
}

/**
 * Line Waveform
 * Linea continua fluida
 */
export function renderLine(ctx, data, colors) {
  const { width, height, bars } = data;
  const { waveformColor, waveformBgColor } = colors;
  
  // Pulisci canvas
  ctx.clearRect(0, 0, width, height);
  
  // Sfondo
  ctx.fillStyle = waveformBgColor;
  ctx.fillRect(0, 0, width, height);
  
  if (bars.length === 0) return;
  
  // Linea continua
  ctx.strokeStyle = waveformColor;
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  
  ctx.beginPath();
  
  // Primo punto
  const firstBar = bars[0];
  ctx.moveTo(firstBar.x + firstBar.width / 2, firstBar.y);
  
  // Interpolazione smooth con curve di Bézier
  for (let i = 0; i < bars.length - 1; i++) {
    const current = bars[i];
    const next = bars[i + 1];
    
    const currentX = current.x + current.width / 2;
    const currentY = current.y;
    const nextX = next.x + next.width / 2;
    const nextY = next.y;
    
    // Punto di controllo per curve smooth
    const cpX = (currentX + nextX) / 2;
    
    ctx.quadraticCurveTo(currentX, currentY, cpX, (currentY + nextY) / 2);
  }
  
  // Ultimo punto
  const lastBar = bars[bars.length - 1];
  ctx.lineTo(lastBar.x + lastBar.width / 2, lastBar.y);
  
  ctx.stroke();
  
  // Fill area sotto la linea (opzionale, elegante)
  ctx.lineTo(lastBar.x + lastBar.width / 2, height);
  ctx.lineTo(firstBar.x + firstBar.width / 2, height);
  ctx.closePath();
  
  // Gradiente fill
  const gradient = ctx.createLinearGradient(0, 0, 0, height);
  gradient.addColorStop(0, waveformColor.replace(')', ', 0.3)').replace('rgb', 'rgba'));
  gradient.addColorStop(1, waveformColor.replace(')', ', 0.05)').replace('rgb', 'rgba'));
  ctx.fillStyle = gradient;
  ctx.fill();
}

/**
 * Blocks Waveform
 * Blocchi pixel-art style
 */
export function renderBlocks(ctx, data, colors) {
  const { width, height, bars } = data;
  const { waveformColor, waveformBgColor } = colors;
  
  // Pulisci canvas
  ctx.clearRect(0, 0, width, height);
  
  // Sfondo
  ctx.fillStyle = waveformBgColor;
  ctx.fillRect(0, 0, width, height);
  
  // Blocchi più larghi
  const blockWidth = bars.length > 0 ? bars[0].width * 3 : 6;
  const blockGap = 2;
  
  bars.forEach((bar, index) => {
    // Raggruppa ogni 3 barre
    if (index % 3 !== 0) return;
    
    // Media delle prossime 3 barre
    let avgHeight = bar.height;
    if (index + 1 < bars.length) avgHeight = (avgHeight + bars[index + 1].height) / 2;
    if (index + 2 < bars.length) avgHeight = (avgHeight + bars[index + 2].height) / 3;
    
    // Quantizza altezza (effetto blocchi)
    const blockHeight = Math.floor(avgHeight / 10) * 10;
    
    // Intensità per colore
    const intensity = bar.intensity || 0.5;
    
    // Colore basato su intensità
    if (intensity < 0.3) {
      ctx.fillStyle = waveformBgColor;
    } else if (intensity < 0.6) {
      ctx.fillStyle = waveformColor.replace(')', ', 0.5)').replace('rgb', 'rgba');
    } else {
      ctx.fillStyle = waveformColor;
    }
    
    ctx.fillRect(
      bar.x,
      height - blockHeight,
      blockWidth - blockGap,
      blockHeight
    );
  });
}

/**
 * Gradient Bars Waveform
 * Barre con gradiente basato su intensità
 */
export function renderGradient(ctx, data, colors) {
  const { width, height, bars } = data;
  const { waveformColor, waveformBgColor } = colors;
  
  // Pulisci canvas
  ctx.clearRect(0, 0, width, height);
  
  // Sfondo
  ctx.fillStyle = waveformBgColor;
  ctx.fillRect(0, 0, width, height);
  
  // Barre con gradiente
  bars.forEach(bar => {
    const intensity = bar.intensity || 0.5;
    
    // Crea gradiente verticale per ogni barra
    const gradient = ctx.createLinearGradient(bar.x, bar.y, bar.x, bar.y + bar.height);
    
    // Colori basati su intensità
    if (intensity > 0.8) {
      // Alto volume - rosso
      gradient.addColorStop(0, '#ef4444');
      gradient.addColorStop(1, '#dc2626');
    } else if (intensity > 0.6) {
      // Medio-alto - arancione
      gradient.addColorStop(0, '#f59e0b');
      gradient.addColorStop(1, '#d97706');
    } else if (intensity > 0.4) {
      // Medio - giallo
      gradient.addColorStop(0, '#eab308');
      gradient.addColorStop(1, '#ca8a04');
    } else {
      // Basso - verde/cyan
      gradient.addColorStop(0, '#06b6d4');
      gradient.addColorStop(1, '#0891b2');
    }
    
    ctx.fillStyle = gradient;
    ctx.fillRect(bar.x, bar.y, bar.width, bar.height);
  });
}

/**
 * Registry dei renderer disponibili
 */
export const WAVEFORM_RENDERERS = {
  bar: renderBar,
  mirrored: renderMirrored,
  line: renderLine,
  blocks: renderBlocks,
  gradient: renderGradient
};

/**
 * Ottieni renderer per tipo
 */
export function getRenderer(type = 'bar') {
  return WAVEFORM_RENDERERS[type] || WAVEFORM_RENDERERS.bar;
}
